import {
	AfterViewInit,
	Component,
	HostListener,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs/Subject';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { AccountServiceWebProvider } from '@web-services/user/account.service';
import { GraphsDataServiceProvider } from '@web-components/dashboard/graphs-block/graphs.data.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { GraphsService } from '@web-services/graph/graphs.service';
import {
	BootstrapSettings,
	CustomizedLabels,
	GraphsFilterObject,
	GraphsTotalAmounts,
	JobType,
	PieChartData,
	RevenueComparisonRangesObject,
	SelectedDateRange
} from '@models/models';
import { MessageService } from '@services/helpers/message-service/message.service';
import { filter, takeUntil } from 'rxjs/operators';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';
import { SettingsProvider } from '@services/settings/settings';
import { ViewAllLeadSourcesModalComponent } from '@web-components/dashboard/modals/view-all-lead-sources/view-all-lead-sources-modal';
import { SNModalService } from '@web-services/modal/snModal.service';
import { LeadSourcesDataService } from '@services/lead-sources/lead-sources.data.service';
import { PieGraphService } from '@web-services/graph/pie-graph.service';

import moment = require('moment');

@Component({
	selector: 'graphs-block',
	template: require('./graphs-block.html'),
	styles: [require('./graphs-block.scss')]
})
export class GraphsBlockComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() jobTypes: JobType[];

	shootsTabName: string;

	companyId: number;
	activeTab: string;
	currency: string;
	fromDate: string;
	toDate: string;
	graphTotal: GraphsTotalAmounts = {
		lead: null,
		shoot: null,
		payments: null,
		paymentsDue: null
	};
	leadsData;
	acceptedLeadData;
	shootsData;
	paymentsData;
	paymentsDueData;
	revenueData;
	leadSourceData: PieChartData;
	minDate: Moment;
	maxDate: Moment;
	unsubscribe: Subject<void> = new Subject<void>();
	dateRangePreset: string;
	selectedJobType: number;
	selectedRange: SelectedDateRange;
	blockWidth: number;
	leadSourcesAvailable: boolean;

	private selectedFilters: GraphsFilterObject = {
		fromDate: null,
		toDate: null,
		jobTypeId: null
	};

	constructor(
		private http: HttpClient,
		private graphsDataService: GraphsDataServiceProvider,
		private timeService: TimeServiceProvider,
		private graphsService: GraphsService,
		private pieGraphService: PieGraphService,
		private accountService: AccountServiceWebProvider,
		private brandsDataService: BrandsDataServiceProvider,
		private settingsService: SettingsProvider,
		private messageService: MessageService,
		private modalService: SNModalService,
		private leadSourcesService: LeadSourcesDataService
	) {}

	ngOnInit(): void {
		this.subscribeToSettingsChange();
		this.subscribeToCustomizeLabelsChange();
		this.configureCommunicationAdapter();
		this.subscribeToBrandsChange();
		this.getData();
	}

	ngAfterViewInit(): void {
		this.subscribeToLeadSourceUpdate();
	}

	ngOnDestroy(): void {
		this.graphsService.clear();
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	openViewAllModal(): void {
		const modal = this.modalService.openModal(
			ViewAllLeadSourcesModalComponent,
			{ windowClass: 'ng-bootstrap-modal modal-visible modal-wide' }
		);
		modal.componentInstance.modalPayload = {
			jobTypes: this.jobTypes,
			selectedFilters: this.selectedFilters,
			minDate: this.minDate,
			maxDate: this.maxDate,
			selectedRange: this.selectedRange
		};
	}

	// GRAPHS DATA MANIPULATION

	applyChosenJobType(): void {
		if (this.selectedJobType === -1) {
			this.selectedFilters.jobTypeId = null;
		} else {
			this.selectedFilters.jobTypeId = this.selectedJobType;
		}
	}

	applyChosenDateRange(): void {
		this.selectedRange = {
			startDate: new Date(this.fromDate).toISOString(),
			endDate: new Date(this.toDate).toISOString()
		};

		this.selectedFilters.fromDate = this.selectedRange.startDate.slice(0, 10);
		this.selectedFilters.toDate = this.selectedRange.endDate.slice(0, 10);
	}

	// VIEW EVENTS HANDLERS

	onChangeActiveTab(newActiveTab: string): void {
		if (newActiveTab !== this.activeTab) {
			this.activeTab = newActiveTab;
			localStorage.setItem(`dashboardActiveTab${this.companyId}`, newActiveTab);

			this.paintGraphsOnTab(this.activeTab);
		}
	}

	onJobTypeChange(newJobType: string): void {
		this.selectedJobType = parseInt(newJobType);
		localStorage.setItem(`dashboardJobType${this.companyId}`, newJobType);

		this.applyChosenJobType();

		this.applyFiltersToGraphsOn(['leads', 'shoots', 'payments', 'lead-source']);
	}

	onDateRangePresetToggle(newRangePreset: string): void {
		if (newRangePreset !== this.dateRangePreset) {
			this.dateRangePreset = newRangePreset;

			localStorage.setItem(
				`presetDashboardDateRange${this.companyId}`,
				this.dateRangePreset
			);
			localStorage.removeItem(`dashboardFromDate${this.companyId}`);
			localStorage.removeItem(`dashboardToDate${this.companyId}`);

			this.setDateRangePreset(this.dateRangePreset);
			this.applyChosenDateRange();
			this.applyFiltersToGraphsOn([
				'leads',
				'shoots',
				'payments',
				'lead-source'
			]);
		}
	}

	setCustomDateRange(newDateRange: {
		startDate: Moment;
		endDate: Moment;
	}): void {
		if (moment.isMoment(newDateRange.startDate)) {
			this.fromDate = this.timeService.getDateString(
				newDateRange.startDate.toDate()
			);
			this.toDate = this.timeService.getDateString(
				newDateRange.endDate.toDate()
			);

			this.dateRangePreset = null;
			localStorage.removeItem(`presetDashboardDateRange${this.companyId}`);
			localStorage.setItem(`dashboardFromDate${this.companyId}`, this.fromDate);
			localStorage.setItem(`dashboardToDate${this.companyId}`, this.toDate);

			this.applyChosenDateRange();
			this.applyFiltersToGraphsOn([
				'leads',
				'shoots',
				'payments',
				'lead-source'
			]);
		}
	}

	// PRESET DATE RANGES

	setDateRangePreset(dateRangePreset: string): void {
		switch (dateRangePreset) {
			case '7': {
				this.setRangeToLastDays(7);
				break;
			}

			case '30': {
				this.setRangeToLastDays(30);
				break;
			}

			case 'm': {
				this.setMonthToDateRange();
				break;
			}

			case 'y': {
				this.setYearToDateRange();
				break;
			}

			default: {
				break;
			}
		}
	}

	setRangeToLastDays(daysBefore: number): void {
		const today = new Date();
		const past = new Date();

		const difference = today.getDate() - daysBefore;
		past.setDate(difference);

		this.fromDate = this.timeService.getDateString(past);
		this.toDate = this.timeService.getDateString(today);
	}

	setMonthToDateRange(): void {
		const today = new Date();
		const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

		this.fromDate = this.timeService.getDateString(monthStart);
		this.toDate = this.timeService.getDateString(today);
	}

	setYearToDateRange(): void {
		const today = new Date();
		const yearStart = new Date(today.getFullYear(), 0, 1);

		this.fromDate = this.timeService.getDateString(yearStart);
		this.toDate = this.timeService.getDateString(today);
	}

	/**
	 * @method applyFiltersToGraphsOn
	 * @desc gets an array of tabs, calculates dynamic number of API calls
	 * and success calbacks, then runs them. When all is done, it calls a
	 * method to paint graph on active tab
	 * @param {string[]} tabs An array of tabs we need fetch new data for
	 * */
	applyFiltersToGraphsOn(tabs: string[]): void {
		const [graphsInfoObservables, successCallbacks]: [
			Observable<any>[],
			Function[]
		] = this.getGraphsObservablesAndSuccessCallbacks(tabs);

		Observable.zip(...graphsInfoObservables)
			.finally((): void => {
				this.paintGraphsOnTab(this.activeTab);
			})
			.subscribe(
				(data): void => {
					successCallbacks.forEach((callback, index) => {
						callback(data[index]);
					});
				},
				(error): void => {
					this.messageService.showDefaultErrorMessage(error);
				}
			);
	}

	// SUCCESS CALLBACKS

	leadsGraphInfoSuccessCallback(leadData): void {
		this.leadsData = JSON.parse(JSON.stringify(leadData.data));
		this.graphTotal.lead = JSON.parse(JSON.stringify(leadData.total));
	}

	acceptedLeadGraphInfoSuccessCallback(acceptedLeadData): void {
		this.acceptedLeadData = JSON.parse(JSON.stringify(acceptedLeadData.data));
	}

	shootsGraphInfoSuccessCallback(shootData): void {
		this.shootsData = JSON.parse(JSON.stringify(shootData.data));
		this.graphTotal.shoot = JSON.parse(JSON.stringify(shootData.total));
	}

	paymentsGraphInfoSuccessCallback(paymentData): void {
		this.paymentsData = JSON.parse(JSON.stringify(paymentData.data));
		this.graphTotal.payments = JSON.parse(JSON.stringify(paymentData.total));
	}

	paymentsDueGraphInfoSuccessCallback(paymentDueData): void {
		this.paymentsDueData = JSON.parse(JSON.stringify(paymentDueData.data));
		this.graphTotal.paymentsDue = JSON.parse(
			JSON.stringify(paymentDueData.total)
		);
	}

	revenueGraphInfoSuccessCallback(revenueData): void {
		this.revenueData = JSON.parse(JSON.stringify(revenueData));
	}

	leadSourcesGraphSuccessCallback(leadSourceData): void {
		this.leadSourceData = this.pieGraphService.preparePieChartData(
			leadSourceData.data
		);
	}

	/**
	 * @method getGraphsObservablesAndSuccessCallbacks
	 * @desc gets an array of tabs and depending on it dynamically populates
	 * two arrays with needed observables and success callbacks lists.
	 * @param {string[]} tabs An array of tabs we need fetch new data for
	 * @returns {[Observable<any>[], Function[]]} [observables, callbacks]
	 * an array of two arrays with generated observables and success callbacks
	 * */
	private getGraphsObservablesAndSuccessCallbacks(
		tabs: string[]
	): [Observable<any>[], Function[]] {
		const observables: Observable<any>[] = [];
		const callbacks: Function[] = [];

		tabs.forEach((tab): void => {
			switch (tab) {
				case 'leads':
					observables.push(
						this.graphsDataService.getLeadsGraphInfo(this.selectedFilters)
					);
					callbacks.push(this.leadsGraphInfoSuccessCallback.bind(this));

					observables.push(
						this.graphsDataService.getAcceptedLeadGraphInfo(
							this.selectedFilters
						)
					);
					callbacks.push(this.acceptedLeadGraphInfoSuccessCallback.bind(this));
					break;
				case 'shoots':
					observables.push(
						this.graphsDataService.getShootsGraphInfo(this.selectedFilters)
					);
					callbacks.push(this.shootsGraphInfoSuccessCallback.bind(this));
					break;
				case 'payments':
					observables.push(
						this.graphsDataService.getPaymentsGraphInfo(this.selectedFilters)
					);
					callbacks.push(this.paymentsGraphInfoSuccessCallback.bind(this));

					observables.push(
						this.graphsDataService.getPaymentsDueGraphInfo(
							this.selectedFilters,
							'unpaid'
						)
					);
					callbacks.push(this.paymentsDueGraphInfoSuccessCallback.bind(this));

					observables.push(
						this.graphsDataService.getAllTimeStatisticsGraphInfo(
							this.selectedFilters
						)
					);

					callbacks.push(this.revenueGraphInfoSuccessCallback.bind(this));

					break;
				case 'lead-source':
					observables.push(
						this.leadSourcesService.getLeadSourceGraphData(this.selectedFilters)
					);
					callbacks.push(this.leadSourcesGraphSuccessCallback.bind(this));
					break;
				default:
					break;
			}
		});

		return [observables, callbacks];
	}

	private paintGraphsOnTab(tab: string): void {
		switch (tab) {
			case 'lead_menu':
				this.graphsService.createLeadData(
					this.leadsData,
					this.acceptedLeadData,
					this.companyId
				);
				break;
			case 'shoot_menu':
				this.graphsService.createShootData(this.shootsData, this.companyId);
				break;
			case 'payment_menu':
				this.graphsService.createPaymentsData(
					this.paymentsData,
					this.paymentsDueData,
					this.currency,
					this.companyId
				);
				break;
			case 'revenue_menu':
				this.graphsService.createRevenueData(
					this.revenueData,
					this.currency,
					this.companyId
				);
				break;
			case 'lead_sources_menu':
				if (this.leadSourceData) {
					this.graphsService.clear();
					this.pieGraphService.drawTabPieChart(this.leadSourceData, 'chart', {
						legendRowLength: 6,
						pattern: 'NAME'
					});
				} else {
					GraphsService.removeLegend();
				}

				break;
			default:
				break;
		}
	}

	// LOCAL STORAGE

	private getActiveTabFromLocalStorage(): void {
		const localStorageItem = `dashboardActiveTab${this.companyId}`;
		this.blockWidth = window.innerWidth;

		if (!localStorage.getItem(localStorageItem)) {
			localStorage.setItem(localStorageItem, 'lead_menu');
			this.activeTab = 'lead_menu';
		} else if (
			localStorage.getItem(localStorageItem) === 'lead_sources_menu' &&
			this.blockWidth > 992
		) {
			localStorage.setItem(localStorageItem, 'revenue_menu');
			this.activeTab = 'revenue_menu';
		} else {
			this.activeTab = localStorage.getItem(localStorageItem);
		}
	}

	private getDateRangeFromLocalStorage(): void {
		if (localStorage.getItem(`presetDashboardDateRange${this.companyId}`)) {
			this.dateRangePreset = localStorage.getItem(
				`presetDashboardDateRange${this.companyId}`
			);
			this.setDateRangePreset(this.dateRangePreset);
		} else if (
			localStorage.getItem(`dashboardFromDate${this.companyId}`) &&
			localStorage.getItem(`dashboardToDate${this.companyId}`)
		) {
			this.fromDate = localStorage.getItem(
				`dashboardFromDate${this.companyId}`
			);
			this.toDate = localStorage.getItem(`dashboardToDate${this.companyId}`);
		} else {
			this.setRangeToLastDays(7);
			this.dateRangePreset = '7';
			localStorage.setItem(`presetDashboardDateRange${this.companyId}`, '7');
		}
	}

	private getActiveJobTypeFromLocalStorage(): void {
		this.selectedJobType =
			parseInt(localStorage.getItem(`dashboardJobType${this.companyId}`)) || -1;
	}

	// HELPERS

	private getCurrencySymbol(): void {
		this.accountService.userDataObservable
			.takeUntil(this.unsubscribe)
			.subscribe((data): void => {
				if (data) {
					this.currency = data.currency.symbol;
				}
			});
	}

	private getCompanyId(): void {
		this.accountService.companyIdObservable
			.takeUntil(this.unsubscribe)
			.subscribe(data => {
				this.companyId = data;
				this.getActiveTabFromLocalStorage();
				this.getDateRangeFromLocalStorage();
				this.getActiveJobTypeFromLocalStorage();
			});
	}

	private setExtremeDates(): void {
		const tenYearsForward: Date = new Date();
		const tenYearsBackward: Date = new Date();

		this.minDate = moment(
			tenYearsBackward.setFullYear(tenYearsBackward.getFullYear() - 10)
		);
		this.maxDate = moment(
			tenYearsForward.setFullYear(tenYearsForward.getFullYear() + 10)
		);
	}

	private setDateRangesForRevenueComparison(): RevenueComparisonRangesObject {
		const thisYear = new Date().getFullYear();

		return {
			thisYear: {
				fromDate: this.timeService.getDateString(new Date(thisYear, 0, 1)),
				toDate: this.timeService.getDateString(new Date(thisYear, 11, 31)),
				jobTypeId: this.selectedFilters.jobTypeId
			},
			lastYear: {
				fromDate: this.timeService.getDateString(new Date(thisYear - 1, 0, 1)),
				toDate: this.timeService.getDateString(new Date(thisYear - 1, 11, 31)),
				jobTypeId: this.selectedFilters.jobTypeId
			},
			nextYear: {
				fromDate: this.timeService.getDateString(new Date(thisYear + 1, 0, 1)),
				toDate: this.timeService.getDateString(new Date(thisYear + 1, 11, 31)),
				jobTypeId: this.selectedFilters.jobTypeId
			}
		};
	}

	private configureCommunicationAdapter(): void {
		window.snCommunicationAdapter.registerMethod(
			'updateGraphsModel',
			this.applyFiltersToGraphsOn.bind(this)
		);
	}

	private getData(): void {
		this.setExtremeDates();
		this.getCurrencySymbol();
		this.getCompanyId();
		this.applyChosenJobType();
		this.applyChosenDateRange();
		this.applyFiltersToGraphsOn(['leads', 'shoots', 'payments', 'lead-source']);
	}

	private subscribeToBrandsChange(): void {
		this.brandsDataService.brandsChange
			.pipe(
				takeUntil(this.unsubscribe),
				filter((data): boolean => !!data)
			)
			.subscribe((): void => this.getData());
	}

	private subscribeToSettingsChange(): void {
		this.settingsService.timeFormatSettings
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(settings: BootstrapSettings): boolean =>
					(this.leadSourcesAvailable = settings.leadSourcesAvailable)
			);
	}

	private subscribeToCustomizeLabelsChange(): void {
		this.settingsService.customizeLabelsObservable
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(customizeLabels: CustomizedLabels): string =>
					(this.shootsTabName = customizeLabels.shootsLabel)
			);
	}

	private subscribeToLeadSourceUpdate(): void {
		this.leadSourcesService.leadSourcesEventTrigger
			.pipe(
				takeUntil(this.unsubscribe),
				filter((state: boolean): boolean => state)
			)
			.subscribe((): void => this.applyFiltersToGraphsOn(['lead-source']));
	}

	@HostListener('window:resize', ['$event'])
	onResize(event): void {
		this.reactToWidthChange(event.target.innerWidth);
	}

	private reactToWidthChange(width: number): void {
		this.blockWidth = width;
		if (width >= 992 && this.activeTab === 'lead_sources_menu') {
			this.onChangeActiveTab('revenue_menu');

			requestAnimationFrame((): void => PieGraphService.resizePieChart());
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/dashboard/graphs-block/graphs-block.ts